// Per-page tour data. Each PAGE_TOURS[key] is the slim tour shown when the
// user is on that page, 3-5 steps, action-oriented, pointing at DOM elements
// that actually exist on this page.
//
// New behavior (vs the old single 30-step global tour):
//  - Tours are scoped to the current page. Can't point at a button that
//    isn't on this page = can't dangle a tooltip in the void.
//  - Each tour auto-runs the first time a user lands on its page. After that
//    it's triggered manually by the "?" button in the page header.
//  - Re-triggerable anytime via the same button, same tour, no relearn.
//
// Each step:
//   - title (string)
//   - content (string)
//   - target (CSS selector, usually data-tour="..."; null = centered tooltip)
//
// Pages without a tour key here = no tour. Add one when the page is worth
// orienting a brand-new user on.

export const PAGE_TOURS = {
  dashboard: [
    {
      id: 'dash-1',
      title: 'Welcome to Sports Card Network',
      content: "This is your home base. Once you've got cards listed and orders coming in, your key numbers and daily to-dos show up right here.",
      target: null,
      keywords: ['dashboard', 'home', 'getting started', 'overview'],
    },
    {
      id: 'dash-2',
      title: 'First, get your cards in',
      content: "Pick whichever fits you: connect eBay to import your whole catalog, paste cert numbers for graded slabs, or just scan a photo. Any one of these gets you rolling.",
      target: '[data-tour="dash-add-first"]',
      keywords: ['add first card', 'get started', 'add cards', 'import catalog', 'scan a card'],
    },
    {
      id: 'dash-3',
      title: 'Your setup checklist',
      content: "This little checklist walks you through the essentials, connect eBay, set your ship-from address, add a cost, see your real P&L. Check them off as you go, and it disappears once you're done.",
      target: '.setup-widget',
      keywords: ['setup', 'checklist', 'complete setup', 'onboarding', 'getting set up'],
    },
    {
      id: 'dash-4',
      title: 'Everything else is one click away',
      content: "The menu on the left has it all: Inventory, Sales Channels, Orders, Accounting, Grading, and more. Each page gives you a quick walkthrough the first time you open it, so you're never guessing.",
      target: null,
      keywords: ['navigation', 'menu', 'where is', 'find a page', 'how do i get to'],
    },
  ],

  inventory: [
    {
      id: 'inv-1',
      title: 'This is your inventory',
      content: "Every card you own lives here: photos, grades, cost, market value, status. Search or filter it any way you want.",
      target: null,
      keywords: ['inventory', 'my cards', 'where are my cards', 'card list'],
    },
    {
      id: 'inv-2',
      title: 'Add your cards',
      content: "Everything starts here. Scan a photo, paste cert numbers, import your whole eBay catalog, or add a card by hand.",
      target: '[data-tour="add-card-btn"]',
      keywords: ['add a card', 'add inventory', 'add cards', 'how do i add a card', 'new card'],
    },
    {
      id: 'inv-3',
      title: 'Sell in person',
      content: "Sold a card at a show or in a DM? Hit Record Sale, tap the cards, take any payment. It leaves inventory, lands in your books, and comes off every channel.",
      target: '[data-tour="record-sale-btn"]',
      keywords: ['record a sale', 'sell in person', 'sold a card', 'card show sale', 'sell a card', 'point of sale', 'pos'],
    },
    {
      id: 'inv-4',
      title: 'Your own storefront',
      content: "Flip this on to get your own public store, a link you send buyers so they buy straight from you.",
      target: '[data-tour="storefront-toggle"]',
      keywords: ['storefront', 'my own store', 'public store', 'store link', 'sell on my own site'],
    },
    {
      id: 'inv-5',
      title: 'Find any card fast',
      content: "Search by player, team, set, or card number, and use the tabs to filter by listed, drafts, or sold.",
      target: '[data-tour="inv-search"]',
      keywords: ['search inventory', 'find a card', 'filter cards', 'search my cards'],
    },
    {
      id: 'inv-6',
      title: 'Open a card',
      content: "Click any card to open the editor: set your price, add your cost, and list it to eBay and every other channel in one click.",
      target: '[data-tour="inventory-grid"]',
      keywords: ['open a card', 'edit a card', 'card details', 'change a card'],
    },
    {
      id: 'inv-7',
      title: 'Do it in bulk',
      content: "Check off several cards and this Actions button lights up: list, reprice, or write AI descriptions for all of them at once.",
      target: '[data-tour="bulk-actions-btn"]',
      keywords: ['bulk', 'multiple cards', 'bulk list', 'select cards', 'list many cards', 'ai descriptions'],
    },
  ],

  addinventory: [
    {
      id: 'add-1',
      title: 'Pick how to add cards',
      content: "There's a few ways to get cards in. Here's when to use each, and you can mix and match anytime.",
      target: null,
      keywords: ['add inventory', 'add a card', 'ways to add cards', 'how do i add cards'],
    },
    {
      id: 'add-2',
      title: 'Start here: AI Scan',
      content: "Snap a photo and the AI fills in the player, year, set, parallel, and grade. The fastest way for most cards, raw or graded.",
      target: '[data-tour="add-ai-scan"]',
      keywords: ['scan a card', 'ai scan', 'scan', 'take a photo of a card', 'scan to add'],
    },
    {
      id: 'add-3',
      title: 'Got graded slabs?',
      content: "Paste your PSA, BGS, SGC, or CGC cert numbers and we pull the grade, card details, and photos for you.",
      target: '[data-tour="add-cert"]',
      keywords: ['cert number', 'graded card', 'add a slab', 'import certs', 'psa cert', 'add graded'],
    },
    {
      id: 'add-4',
      title: 'Already sell on eBay?',
      content: "Pull your whole active eBay catalog into SCN in one click, titles, photos, and prices included.",
      target: '[data-tour="add-ebay"]',
      keywords: ['import ebay', 'ebay listings', 'import my catalog', 'import from ebay'],
    },
    {
      id: 'add-5',
      title: 'Or add one by hand',
      content: "No photo, or an older card the AI can't read? Create it manually and fill in the details yourself.",
      target: '[data-tour="add-manual"]',
      keywords: ['add manually', 'by hand', 'enter a card', 'manual entry', 'create a card'],
    },
  ],

  editcard: [
    {
      id: 'ec-1',
      title: 'The card editor',
      content: "Everything about one card lives here. This is where you get it ready to sell.",
      target: null,
      keywords: ['edit a card', 'card editor', 'change a card', 'card details'],
    },
    {
      id: 'ec-2',
      title: 'Photos',
      content: "Add a front and back photo. Clear photos sell faster, and some channels require them before you can list.",
      target: '#ereq-photo',
      keywords: ['add photos', 'photos', 'card pictures', 'upload a photo'],
    },
    {
      id: 'ec-3',
      title: 'Card details',
      content: "The AI filled these in from your scan. Fix anything that's off, or type in details for older cards it couldn't read.",
      target: '#ereq-sport',
      keywords: ['edit card details', 'fix card info', 'change fields', 'card information'],
    },
    {
      id: 'ec-4',
      title: 'Price and cost',
      content: "Set your asking price, and add what you paid. Your cost is what makes your profit real over in Accounting.",
      target: '#ereq-price',
      keywords: ['price a card', 'set price', 'add cost', 'cost basis', 'what i paid'],
    },
    {
      id: 'ec-5',
      title: 'List it everywhere',
      content: "Check every place you want this card listed: eBay, your storefront, and more. It goes live when you hit Save & List.",
      target: '[data-tour="ec-channels"]',
      keywords: ['list a card', 'list on ebay', 'where to list', 'sales channels', 'cross list', 'list to ebay'],
    },
    {
      id: 'ec-6',
      title: 'Graded or consignment?',
      content: "Graded slab? Turn on Grading to add the cert and grade. Selling it for someone else? Turn on Consignment to track your split.",
      target: '[data-tour="ec-grading"]',
      keywords: ['mark as graded', 'graded toggle', 'mark consignment', 'consignment card'],
    },
    {
      id: 'ec-7',
      title: 'Save it',
      content: "Save keeps it a private draft. Save & List publishes it to every channel you checked, all at once.",
      target: '[data-tour="ec-save-list"]',
      keywords: ['save a card', 'save and list', 'publish a card', 'list it live'],
    },
  ],

  // NOTE: step.keywords power the AI-assistant single-step trigger, a question
  // like "how do I buy a label" fires just that spotlight (see matchTourStep).
  orders: [
    {
      id: 'ord-1',
      title: 'Every sale in one place',
      content: "eBay orders sync every couple minutes, and sales you record in person show up here too. Real fees and real profit, not estimates.",
      target: null,
      keywords: ['orders', 'my sales', 'where are my orders', 'sales'],
    },
    {
      id: 'ord-2',
      title: 'What needs shipping',
      content: "New orders waiting to go out live under Needs Shipping, and your to-ship count is always up top so nothing slips.",
      target: '[data-tour="orders-tabs"]',
      keywords: ['needs shipping', 'orders to ship', 'unshipped', 'what to ship'],
    },
    {
      id: 'ord-3',
      title: 'Buy a shipping label',
      content: "Open an order that needs shipping and hit Buy Label: compare USPS, UPS, and FedEx rates, buy at a discount, and it's marked shipped for you.",
      target: null,
      keywords: ['buy a label', 'shipping label', 'ship an order', 'print a label', 'buy postage'],
    },
    {
      id: 'ord-4',
      title: 'Receipts and invoices',
      content: "Open any order to print a receipt or email an invoice to the buyer. On-account sales show a Mark Paid button until they settle up.",
      target: null,
      keywords: ['print receipt', 'email invoice', 'receipt', 'invoice', 'mark paid'],
    },
    {
      id: 'ord-5',
      title: 'Import or export orders',
      content: "The Actions menu imports orders from a spreadsheet or exports your sales, handy when you're moving over from another tool.",
      target: '[data-tour="orders-actions"]',
      keywords: ['import orders', 'export orders', 'import sales', 'orders csv', 'export sales'],
    },
    {
      id: 'ord-6',
      title: 'Find any order',
      content: "Search by card, buyer, or platform to jump straight to the order you need.",
      target: '[data-tour="orders-search"]',
      keywords: ['search orders', 'find an order', 'look up an order'],
    },
  ],

  // This tour DRIVES the page: each step's `clickBefore` switches the tab so the
  // user actually sees Statements / Expenses / Tax Center, not just reads about them.
  accounting: [
    {
      id: 'acc-1',
      title: 'Your whole business, one bottom line',
      content: "Right here is your real profit: revenue minus your actual fees, card cost, and expenses. Across every channel, eBay, in-person, storefront, not just eBay.",
      target: '[data-tour="acct-overview-pnl"]',
      clickBefore: '[data-tour="acct-tab-overview"]',
      keywords: ['accounting', 'profit', 'p and l', 'pnl', 'my numbers', 'bottom line', 'overall profit'],
    },
    {
      id: 'acc-2',
      title: 'Statements',
      content: "Now you're in Statements: your full P&L, Balance Sheet, and Cash Flow. Real financial statements you could hand straight to an accountant.",
      target: '[data-tour="acct-tab-statements"]',
      clickBefore: '[data-tour="acct-tab-statements"]',
      keywords: ['p and l', 'balance sheet', 'cash flow', 'statements', 'financial reports'],
    },
    {
      id: 'acc-3',
      title: 'Log every expense here',
      content: "This is the big one. Record every business cost: supplies, top loaders, PSA fees, gas to the show. Hit Add Expense. Each one lowers your taxable income.",
      target: '[data-tour="acct-add-expense"]',
      clickBefore: '[data-tour="acct-tab-expenses"]',
      keywords: ['log expenses', 'add expense', 'track expenses', 'business expenses', 'write offs', 'supplies', 'grading fees', 'deductions'],
    },
    {
      id: 'acc-4',
      title: 'Tax Center',
      content: "And come tax time it's all here, your totals already sorted onto the right Schedule C lines. Tax prep in minutes instead of a lost weekend.",
      target: '[data-tour="acct-tab-tax"]',
      clickBefore: '[data-tour="acct-tab-tax"]',
      keywords: ['tax center', 'taxes', 'schedule c', 'tax prep', 'deductions'],
    },
    {
      id: 'acc-5',
      title: 'Consignment flows in here too',
      content: "Selling other people's cards? The Consignment page over in the nav tracks each consignor, their split, and exactly what you owe, and it flows into these books.",
      target: '[data-tour="sidebar-consignment"]',
      keywords: ['consignment', 'consignor', 'selling for others', 'commission', 'split', 'what i owe'],
    },
  ],

  // Grading is the deepest page, the walkthrough is intentionally long. It
  // covers the whole lifecycle: decide -> submit -> import -> scan -> track -> ROI.
  grading: [
    {
      id: 'grd-1',
      title: 'Grading, start to finish',
      content: "Grading is where a lot of profit hides, and where a lot of money gets wasted. This page does two jobs. First, it helps you decide whether a card is even worth grading before you spend a dollar. Second, once cards go out, it tracks every submission from the moment they leave your hands to the day the slab sells, and tells you the real return on each one. Let's walk the whole thing.",
      target: null,
      keywords: ['grading', 'psa', 'submissions', 'graded cards', 'grade a card', 'how does grading work'],
    },
    {
      id: 'grd-2',
      title: 'First: should you even grade it?',
      content: "Start here before you ever mail a card. Tap Should I Grade This?, snap a photo of a raw card, and SCN pulls its raw market value and shows your exact projected profit at a PSA 10, a 9, and an 8, with the grading fee already subtracted. Instead of gambling $30 a card and hoping, you see the math up front and only grade the ones that actually pay.",
      target: '[data-tour="should-i-grade-btn"]',
      keywords: ['should i grade', 'is grading worth it', 'grade this card', 'grading roi', 'worth grading', 'psa 10 profit'],
    },
    {
      id: 'grd-3',
      title: 'Log a submission you’re sending',
      content: "When you're ready to send a batch off, hit New Submission. Pick the grading company (PSA, BGS, SGC) and the service level, then add the cards you're sending, pulling them straight from your inventory so their cost basis carries over. That creates a submission you can track and moves those cards into an 'at the grader' state, so you always know what's out and what it's costing you.",
      target: '[data-tour="new-submission-btn"]',
      keywords: ['new submission', 'log a submission', 'track psa', 'send to psa', 'psa submission', 'grading submission'],
    },
    {
      id: 'grd-4',
      title: 'Import instead of typing',
      content: "The Import menu is the shortcut that saves you the data entry. 'PSA submission form (PDF)' takes the confirmation PDF PSA emails you, reads every card off it, and auto-matches them to cards already in your inventory, building the whole submission in seconds. Then when grades come back, 'PSA grades receipt (CSV)' drops in the results and applies every grade and cert number at once. The Tools menu next to it holds the rest: invoice a consignor from a sales file, fix duplicate submissions, and check PSA status on demand (it also syncs by itself overnight).",
      target: '[data-tour="grading-import"]',
      keywords: ['import psa', 'psa pdf', 'submission form', 'import grades', 'receipt csv', 'cert numbers', 'sync psa', 'slabs back'],
    },
    {
      id: 'grd-5',
      title: 'Scan a slab’s barcode',
      content: "Got physical slabs in front of you? When a card comes back graded, open its submission, tap the card, and hit Scan in the Enter Grade window: it reads the cert barcode right off the PSA, BGS, or SGC case and fills the cert and grade for you. (To add a slab you bought straight to stock, use Scan Slab on the Inventory page.)",
      target: null,
      keywords: ['scan barcode', 'scan a slab', 'cert lookup', 'cert number', 'scan graded card', 'add a slab'],
    },
    {
      id: 'grd-6',
      title: 'Track every submission live',
      content: "Each submission shows its status as it moves, submitted, received, being graded, graded, and shipped back. The Active tab up top is everything still out at the grader; Completed is everything that's back. You'll never again wonder where a PSA order is or lose track of a card that's been gone for two months.",
      target: null,
      keywords: ['submission status', 'where is my submission', 'active submissions', 'track grading', 'grading status'],
    },
    {
      id: 'grd-7',
      title: 'Know the ROI on every grade',
      content: "This is the payoff. For every card you grade, SCN tracks the whole chain, what you paid raw, what grade it came back, and what it finally sold for, so you get real ROI per submission, per company, and per service tier. Over time you'll see exactly which cards and which tiers make you money and which don't, so you stop grading the losers. That's the whole point: grade smarter, not more.",
      target: null,
      keywords: ['grading roi', 'is grading profitable', 'which tier pays off', 'grade to sale', 'grading profit'],
    },
  ],

  consignment: [
    {
      id: 'con-1',
      title: 'Your consignment ledger',
      content: "Selling cards for other people? This is where you track whose cards you're holding, what they've sold, and exactly what you owe each person. No more spreadsheets or sticky notes.",
      target: null,
      keywords: ['consignment', 'consignor', 'selling for others', 'consign', 'how does consignment work'],
    },
    {
      id: 'con-2',
      title: 'Add a consignor',
      content: "Start by adding a consignor: their name, contact, and the commission split you agreed on. Then you assign cards from your inventory to them, and every sale automatically calculates their cut for you.",
      target: '[data-tour="add-consignor-btn"]',
      keywords: ['add consignor', 'new consignor', 'set up consignment', 'commission rate', 'commission split'],
    },
    {
      id: 'con-3',
      title: 'The numbers at a glance',
      content: "Up here: how many consignors you have, how many of their cards you're holding, total consignment sales, and the big one: Owed to Consignors, exactly what you need to pay out right now.",
      target: '[data-tour="consignment-summary"]',
      keywords: ['owed to consignors', 'consignment total', 'how much do i owe', 'consignment summary'],
    },
    {
      id: 'con-4',
      title: 'Pay them out',
      content: "When their cards sell, what you owe shows up under Payouts Due, aged so you see who's been waiting longest. Hit Pay to settle a balance and record it, and you can send each consignor a clean statement of their items, sales, and what they're owed.",
      target: null,
      keywords: ['pay consignor', 'payout', 'record payment', 'consignor statement', 'what i owe', 'settle up'],
    },
  ],

  // Drives through every tab via clickBefore so the user actually sees Sets,
  // Players, Wishlist, and Alerts, not just reads about them.
  collections: [
    {
      id: 'col-1',
      title: 'Your personal collection',
      content: "This is the stuff you keep, separate from what you're selling. Your PC, your graded keepers, set builds, and the cards you're still chasing. Let's run through what's in here.",
      target: null,
      keywords: ['collection', 'personal collection', 'my pc', 'cards i keep', 'portfolio'],
    },
    {
      id: 'col-2',
      title: 'Slice it any way',
      content: "Filter your collection up here: everything, just graded, just raw, or your high-value pieces over $100. Quick way to zero in on what you care about.",
      target: '[data-tour="coll-tabs"]',
      clickBefore: '[data-tour="coll-tab-all"]',
      keywords: ['filter collection', 'graded cards', 'high value cards', 'collection tabs'],
    },
    {
      id: 'col-3',
      title: 'Track its value over time',
      content: "Every card is priced with live market comps, and we track your whole collection's value with history, so you can watch what's appreciating and what's sliding without lifting a finger.",
      target: null,
      keywords: ['collection value', 'portfolio value', 'what is my collection worth', 'value over time', 'appreciating'],
    },
    {
      id: 'col-4',
      title: 'Sets: build and track',
      content: "Building a set? Create it here, auto-match your cards to it, and see your completion percentage, exactly which cards you're missing, and roughly what it'll cost to finish.",
      target: '[data-tour="coll-tab-sets"]',
      clickBefore: '[data-tour="coll-tab-sets"]',
      keywords: ['sets', 'set building', 'set tracking', 'complete a set', 'missing cards', 'set completion'],
    },
    {
      id: 'col-5',
      title: 'Players: collect by name',
      content: "Collect a certain player? The Players tab groups your collection by player, so you can see your whole Curry or Jordan run in one place.",
      target: '[data-tour="coll-tab-players"]',
      clickBefore: '[data-tour="coll-tab-players"]',
      keywords: ['players', 'player collection', 'collect a player', 'by player'],
    },
    {
      id: 'col-6',
      title: 'Wishlist: the cards you want',
      content: "Add the cards you're hunting to your Wishlist, and give each one a budget, the most you'd pay. That budget is what powers your alerts.",
      target: '[data-tour="coll-tab-wishlist"]',
      clickBefore: '[data-tour="coll-tab-wishlist"]',
      keywords: ['wishlist', 'wanted cards', 'cards i want', 'want list', 'budget', 'hunting for a card'],
    },
    {
      id: 'col-7',
      title: 'Price alerts on wanted cards',
      content: "This is the payoff. SCN checks the market every 6 hours and pings you here when a wishlist card drops, spikes, hits your budget, or shows up as a deal on an active listing, so you buy at the right price and never overpay.",
      target: '[data-tour="coll-tab-alerts"]',
      clickBefore: '[data-tour="coll-tab-alerts"]',
      keywords: ['price alerts', 'alerts', 'price drop alert', 'notify me price', 'budget match', 'deal alert'],
    },
  ],

  repricing: [
    {
      id: 'rep-1',
      title: 'Pricing on autopilot',
      content: "Repricing does the tedious part for you, keeping your prices sharp across your whole inventory two ways: nudging cards toward what the market's actually paying, and marking down anything that's been sitting too long.",
      target: null,
      keywords: ['repricing', 'auto price', 'automatic pricing', 'reprice', 'pricing autopilot'],
    },
    {
      id: 'rep-2',
      title: 'Smart Repricing',
      content: "Flip this on and SCN re-checks live eBay comps every week and pushes fresh prices to every channel, always inside your margin floors, so it never dips below your cost or swings too hard.",
      target: '[data-tour="reprice-master"]',
      keywords: ['smart repricing', 'reprice to market', 'match comps', 'auto reprice', 'margin floor'],
    },
    {
      id: 'rep-3',
      title: 'Pick which cards',
      content: "Choose the cards you want repriced, hand-pick specific ones or enroll all your listed cards at once. Once cards are enrolled, a Preview and a Reprice Now button appear so you can run it on demand instead of waiting for the weekly pass.",
      target: '[data-tour="reprice-enroll"]',
      keywords: ['enroll cards', 'which cards reprice', 'choose cards', 'reprice now', 'enroll all listed'],
    },
    {
      id: 'rep-4',
      title: 'Auto-Markdown old stock',
      content: "The other half: cards that just sit. Set day-and-percent tiers, say 5% off after 30 days, 10% after 60, and the stale cards you enroll drop on their own, on every channel, until they finally move.",
      target: '[data-tour="reprice-markdown"]',
      keywords: ['auto markdown', 'markdown', 'aging cards', 'stale inventory', 'drop price', 'old stock', 'discount old cards'],
    },
  ],

  customers: [
    {
      id: 'cust-1',
      title: 'Your customer list, built for you',
      content: "Every buyer turns into a profile here automatically, and you can add people by hand too. It's your address book and CRM in one: contact info, shipping address, full order history, lifetime value, tags, and a way to market to them.",
      target: null,
      keywords: ['customers', 'crm', 'buyers', 'customer list', 'address book'],
    },
    {
      id: 'cust-2',
      title: 'Add a customer and their address',
      content: "Add someone by hand with their name, email, phone, and full shipping address. Once their address is on file you can ship straight to them from an order without retyping anything, and every order they place keeps their profile up to date on its own.",
      target: '[data-tour="add-customer-btn"]',
      keywords: ['add customer', 'new customer', 'customer address', 'shipping address', 'ship to a customer', 'enter address', 'input customer'],
    },
    {
      id: 'cust-3',
      title: 'Find anyone, or a whole segment',
      content: "Search by name, email, or phone, or filter by segment: VIP, wholesale, repeat buyers, brand-new, or lapsed folks who haven't bought in a while. Perfect for deciding who to market to next.",
      target: '[data-tour="customers-filters"]',
      keywords: ['find a customer', 'search customers', 'vip', 'wholesale', 'repeat buyers', 'lapsed', 'segment'],
    },
    {
      id: 'cust-4',
      title: 'Email your buyers',
      content: "Got new inventory? Open Message buyers and send an email blast to a whole segment at once, with your latest listings attached automatically. It's the best free marketing you've got, straight to people who already bought from you.",
      target: '[data-tour="message-buyers-btn"]',
      keywords: ['email blast', 'email customers', 'marketing', 'newsletter', 'email buyers', 'send email'],
    },
    {
      id: 'cust-5',
      title: 'Message eBay buyers too',
      content: "eBay hides buyer emails behind a relay, so for those folks pick eBay message under Message buyers. It reaches them right in eBay's own inbox, where they're already shopping, and your past blasts live under Sent history in the same menu.",
      target: '[data-tour="message-buyers-btn"]',
      keywords: ['ebay message', 'message ebay buyers', 'contact buyer', 'ebay inbox'],
    },
    {
      id: 'cust-6',
      title: 'Dig into anyone, or mail them',
      content: "Click any customer to see their full purchase history, tag them (VIP, wholesale, even a problem buyer), and jot private notes. And anyone with a street address can be exported as a mailing list for postcards or flyers.",
      target: null,
      keywords: ['customer history', 'tags', 'notes', 'vip tag', 'export addresses', 'mailing list', 'flyers'],
    },
  ],

  channels: [
    {
      id: 'ch-1',
      title: 'Connect where you sell',
      content: "Hook up a sales channel here and SCN keeps inventory, orders, and delistings in sync everywhere. Connect once, sell in more places.",
      target: null,
      keywords: ['sales channels', 'connect a channel', 'where to sell', 'add a channel'],
    },
    {
      id: 'ch-2',
      title: 'Start with eBay',
      content: "Click Connect, sign in, and approve, a one-time step. Your whole active catalog imports and your orders sync automatically. eBay is the anchor everything else cross-lists from.",
      target: '[data-tour="connect-ebay-btn"]',
      keywords: ['connect ebay', 'link ebay', 'ebay account', 'import ebay', 'ebay sync'],
    },
    {
      id: 'ch-3',
      title: 'Add the rest the same way',
      content: "Pick any channel on the left (Shopify, Square, MySlabs, Walmart, or TikTok Shop) and connect it the same way. Then a card you list goes to every place you're connected, from one screen.",
      target: '[data-tour="channels-rail"]',
      keywords: ['connect shopify', 'connect square', 'connect walmart', 'connect tiktok', 'connect myslabs', 'cross list', 'more channels'],
    },
  ],

  // Drives through the Settings tabs. (Sales Channels / connect-eBay is its own
  // page + tour now, the old set-2 that pointed at connect-ebay-btn is gone.)
  settings: [
    {
      id: 'set-1',
      title: 'Wire SCN to your business',
      content: "Settings is where you set up the behind-the-scenes stuff: your business profile, shipping, your plan, and what alerts you get. Quick run through what's here.",
      target: null,
      keywords: ['settings', 'set up', 'configure', 'my account settings'],
    },
    {
      id: 'set-2',
      title: 'Your business profile',
      content: "Your business name and contact info. This is what shows on the invoices and receipts you send buyers, so fill it in the way you want customers to see it.",
      target: '[data-tour="set-tab-profile"]',
      clickBefore: '[data-tour="set-tab-profile"]',
      keywords: ['business profile', 'business name', 'my info', 'company details'],
    },
    {
      id: 'set-3',
      title: 'Set your ship-from address',
      content: "This one matters early: your ship-from address is required before you can buy shipping labels or list to eBay. Name, street, city, state, zip, phone. Set it once and you're good.",
      target: '[data-tour="ship-from-card"]',
      clickBefore: '[data-tour="set-tab-shipping"]',
      keywords: ['ship from', 'shipping address', 'return address', 'set up shipping', 'buy labels'],
    },
    {
      id: 'set-4',
      title: 'Your plan and billing',
      content: "Everything about your subscription lives here: see your plan, upgrade or downgrade, update your card, or cancel. Cancel anytime before your trial ends and you're not charged.",
      target: '[data-tour="set-tab-subscription"]',
      clickBefore: '[data-tour="set-tab-subscription"]',
      keywords: ['subscription', 'billing', 'my plan', 'upgrade', 'cancel', 'cancel subscription', 'change plan', 'update card'],
    },
    {
      id: 'set-5',
      title: 'Notifications',
      content: "Control what reaches you, order alerts, product updates, and more. Turn off anything you don't want to hear about.",
      target: '[data-tour="set-tab-notifications"]',
      clickBefore: '[data-tour="set-tab-notifications"]',
      keywords: ['notifications', 'email settings', 'alerts', 'turn off emails', 'unsubscribe'],
    },
  ],
};

// Map of pathname → tour key. Used by PageTour to pick the right tour for
// the current page. Pathnames not in this map = no tour on that page.
export function tourKeyForPath(pathname) {
  if (!pathname) return null;
  // Handle querystrings (e.g. /settings?tab=integrations)
  const [rawPath, rawQuery = ''] = pathname.split('?');
  const p = rawPath.replace(/\/$/, '') || '/';
  // Sales Channels is its own nav item that renders Settings' integrations view
  // (/settings?tab=integrations, or ?channels=1). It gets its own tour, not the
  // generic Settings one. Must be checked before the plain /settings fallback.
  if (p === '/settings' && (/\btab=integrations\b/.test(rawQuery) || /\bchannels=1\b/.test(rawQuery))) return 'channels';
  if (p === '/') return 'dashboard';
  if (p.startsWith('/inventory/edit')) return 'editcard';
  if (p.startsWith('/inventory/add-inventory')) return 'addinventory';
  if (p === '/inventory') return 'inventory';
  // Other /inventory/* pages (add [manual], import, scan-review) have no tour yet.
  if (p.startsWith('/inventory')) return null;
  if (p.startsWith('/orders')) return 'orders';
  if (p.startsWith('/accounting')) return 'accounting';
  if (p.startsWith('/grading')) return 'grading';
  if (p.startsWith('/consignment')) return 'consignment';
  if (p.startsWith('/collections')) return 'collections';
  if (p.startsWith('/repricing')) return 'repricing';
  if (p.startsWith('/customers')) return 'customers';
  if (p.startsWith('/settings')) return 'settings';
  return null;
}

// Where each tour lives, so the assistant can navigate there before launching a
// step. editcard is intentionally absent, it needs a specific card id, so we
// can't deep-link to it generically (we route those questions to inventory).
export const TOUR_PATHS = {
  dashboard: '/',
  inventory: '/inventory',
  addinventory: '/inventory/add-inventory',
  channels: '/settings?tab=integrations',
  orders: '/orders',
  accounting: '/accounting',
  grading: '/grading',
  consignment: '/consignment',
  collections: '/collections',
  repricing: '/repricing',
  customers: '/customers',
  settings: '/settings',
};

// Match a free-text question to the single best tour step, by keyword. Returns
// { tourKey, stepId, path, title } or null. Longer keyword phrases win (more
// specific), so "cancel subscription" beats a bare "cancel". Used by the AI
// assistant to offer a "Show me how" button that fires just that spotlight.
export function matchTourStep(query) {
  const q = (query || '').toLowerCase();
  if (!q.trim()) return null;
  let best = null;
  for (const [tourKey, steps] of Object.entries(PAGE_TOURS)) {
    const path = TOUR_PATHS[tourKey];
    if (!path) continue; // no deep-linkable page (e.g. editcard)
    for (const step of steps) {
      for (const kw of step.keywords || []) {
        const k = kw.toLowerCase();
        if (q.includes(k)) {
          const score = k.length; // longer phrase = more specific match
          if (!best || score > best.score) {
            best = { tourKey, stepId: step.id, path, title: step.title, score };
          }
        }
      }
    }
  }
  return best;
}
